<template>
  <div class="shopping-cart">
    <b-container>
      <h2 class="mb-4">
        <b-icon-bag-check></b-icon-bag-check> Shopping Cart
        <b-badge v-if="cartItems.length" variant="primary">{{ cartItems.length }}</b-badge>
      </h2>

      <b-alert v-if="cartItems.length === 0" variant="info" show>
        <b-icon-inbox></b-icon-inbox> Your cart is empty. Add products to get started!
      </b-alert>

      <div v-else>
        <b-table
          :items="cartItems"
          :fields="fields"
          striped
          hover
          responsive
          class="mb-4"
        >
          <template #cell(image)="data">
            <b-img
              :src="data.item.image"
              height="60"
              class="rounded"
            ></b-img>
          </template>

          <template #cell(name)="data">
            <strong>{{ data.item.name }}</strong>
            <br>
            <small class="text-muted">ID: {{ data.item.id }}</small>
          </template>

          <template #cell(price)="data">
            ${{ data.item.price }}
          </template>

          <template #cell(quantity)="data">
            <b-input-group size="sm">
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="data.item.quantity <= 1"
                @click="decrementQuantity(data.item.id)"
              >
                <b-icon-dash></b-icon-dash>
              </b-button>
              <b-form-input
                v-model.number="data.item.quantity"
                type="number"
                min="1"
                :max="data.item.stock"
                readonly
                class="text-center"
              ></b-form-input>
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="data.item.quantity >= data.item.stock"
                @click="incrementQuantity(data.item.id)"
              >
                <b-icon-plus></b-icon-plus>
              </b-button>
            </b-input-group>
          </template>

          <template #cell(subtotal)="data">
            <strong>${{ (data.item.price * data.item.quantity).toFixed(2) }}</strong>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="danger"
              @click="removeFromCart(data.item.id)"
            >
              <b-icon-trash></b-icon-trash> Remove
            </b-button>
          </template>
        </b-table>

        <b-row class="mb-4">
          <b-col md="8"></b-col>
          <b-col md="4">
            <b-card class="text-right">
              <b-card-title>Order Summary</b-card-title>
              <hr>
              <div class="mb-2">
                <strong>Subtotal:</strong> ${{ cartSubtotal.toFixed(2) }}
              </div>
              <div class="mb-2">
                <strong>Tax (10%):</strong> ${{ cartTax.toFixed(2) }}
              </div>
              <div class="mb-3 pt-2 border-top">
                <h5>
                  <strong>Total:</strong> ${{ cartTotal.toFixed(2) }}
                </h5>
              </div>
              <b-button
                variant="success"
                size="lg"
                block
                @click="checkoutCart"
                class="mb-2"
              >
                <b-icon-credit-card></b-icon-credit-card> Checkout
              </b-button>
              <b-button
                variant="outline-secondary"
                block
                @click="continueShopping"
              >
                <b-icon-arrow-left></b-icon-arrow-left> Continue Shopping
              </b-button>
            </b-card>
          </b-col>
        </b-row>

        <b-alert v-if="checkoutConfirmed" variant="success" show class="mt-3">
          <strong>Order Complete!</strong> Thank you for your purchase. Your order has been confirmed.
        </b-alert>
      </div>
    </b-container>
  </div>
</template>

<script>
import { showEventToast } from '../utils/eventToast';

export default {
  name: 'ShoppingCart',
  inject: ['getGtmProductId'],
  props: {
    cartItems: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      checkoutConfirmed: false,
      fields: [
        { key: 'image', label: 'Image' },
        { key: 'name', label: 'Product' },
        { key: 'price', label: 'Price' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'subtotal', label: 'Subtotal' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },
  computed: {
    cartSubtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },
    cartTax() {
      return this.cartSubtotal * 0.1;
    },
    cartTotal() {
      return this.cartSubtotal + this.cartTax;
    }
  },
  methods: {
    removeFromCart(productId) {
      const index = this.cartItems.findIndex(item => item.id === productId);
      if (index > -1) {
        const productName = this.cartItems[index].name;
        const updatedCart = this.cartItems.filter(item => item.id !== productId);
        this.$emit('update-cart', updatedCart);
        
        this.$bvToast.toast(`${productName} removed from cart`, {
          title: '🗑️ Removed',
          solid: true,
          variant: 'warning',
          toaster: 'b-toaster-top-right'
        });
      }
    },
    incrementQuantity(productId) {
      const item = this.cartItems.find(i => i.id === productId);
      if (item && item.quantity < item.stock) {
        const updatedCart = this.cartItems.map(i => 
          i.id === productId ? { ...i, quantity: i.quantity + 1 } : i
        );
        this.$emit('update-cart', updatedCart);
      }
    },
    decrementQuantity(productId) {
      const item = this.cartItems.find(i => i.id === productId);
      if (item && item.quantity > 1) {
        const updatedCart = this.cartItems.map(i => 
          i.id === productId ? { ...i, quantity: i.quantity - 1 } : i
        );
        this.$emit('update-cart', updatedCart);
      }
    },
    checkoutCart() {
      if (this.cartItems.length === 0) {
        this.$bvToast.toast('Cart is empty!', {
          title: '⚠️ Empty Cart',
          solid: true,
          variant: 'warning',
          toaster: 'b-toaster-top-right'
        });
        return;
      }

      const transactionId = `TXN-${Date.now()}`;

      if (window.dataLayer) {
        const eventData = {
          event: 'purchase',
          ecommerce: {
            transaction_id: transactionId,
            value: this.cartTotal,
            tax: this.cartTax,
            currency: 'USD',
            items: this.cartItems.map(item => ({
              item_id: this.getGtmProductId(item),
              item_name: item.name,
              price: item.price,
              quantity: item.quantity,
              item_category: item.category
            }))
          }
        };
        window.dataLayer.push(eventData);

        // Show toast with expandable details
        showEventToast(this, 'purchase', eventData, '🛒', 'success', {
          transaction_id: transactionId,
          items_count: this.cartItems.length,
          subtotal: `$${this.cartSubtotal.toFixed(2)}`,
          tax: `$${this.cartTax.toFixed(2)}`,
          total: `$${this.cartTotal.toFixed(2)}`
        });
      }

      // Track via vue-gtag
      this.$gtag.event('Purchase_Completed', {
        transaction_id: transactionId,
        value: this.cartTotal,
        currency: 'USD',
        items_count: this.cartItems.length,
        tax: this.cartTax
      });

      this.checkoutConfirmed = true;
      
      setTimeout(() => {
        // Emit event to clear cart
        this.$emit('update-cart', []);
        this.checkoutConfirmed = false;
      }, 3000);

      console.log(`Checkout completed: ${this.cartItems.length} items, Total: $${this.cartTotal.toFixed(2)}`);
    },
    continueShopping() {
      this.$emit('continue-shopping');
    }
  },
  watch: {
    cartItems: {
      handler(newVal) {
        console.log('ShoppingCart received cartItems:', newVal);
      },
      deep: true
    }
  },
  mounted() {
    console.log('ShoppingCart mounted with cartItems:', this.cartItems);
    // Track cart page view
    this.$gtag.pageview({
      pageTitle: 'Shopping Cart',
      pagePath: '/gtm-demo/cart'
    });
  }
};
</script>

<style scoped>
.shopping-cart {
  padding: 20px;
}
</style>
